import { Injectable } from '@angular/core';
import { AdminDashboardService } from './admin-dashboard.service';
import { LoginService } from '../../login/login.service';
import { Observable } from 'rxjs';
import { DashboardCalendarChartCountOfMonth, DashboardCalendarChartStatsOfMonths, DashboardCalendarCountOfMonth, DashboardCalendarOption, DashboardCalendarStatsOfMonths } from 'app/model/dashboardCalendar';
import { ChartDataset, ChartOptions, TooltipItem, TooltipLabelStyle } from 'chart.js';
import swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class DashboardCalendarChartjsBarStatsService {
  private readonly MONTHS: string[] = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ]

  private readonly HOLIDAYS_COLOR: string = '#455cdd'
  private readonly PERSONAL_MATTERS_COLOR: string = '#07B2AF'
  private readonly SICK_LEAVE_COLOR: string = '#8F33C1'
  private readonly PAID_LEAVE_COLOR: string = '#E67E22'
  private readonly REMOTE_WORK_COLOR: string = '#1D1F61'

  private readonly HOLIDAYS_LABEL: string = 'Vacaciones'
  private readonly PERSONAL_MATTERS_LABEL: string = 'Asuntos Propios'
  private readonly SICK_LEAVE_LABEL: string = 'Bajas'
  private readonly PAID_LEAVE_LABEL: string = 'Permisos Retribuidos'
  private readonly REMOTE_WORK_LABEL: string = 'Teletrabajo'

  /**
  * Constructor
  *
  * @param {AdminDashboardService} adminDashboardService
  * @param {LoginService} loginService
  * 
  */
  constructor(
    private adminDashboardService: AdminDashboardService,
    private loginService: LoginService
  ) { }

  public getDashboardCalendarStats(
    calendarOption: DashboardCalendarOption,
    selectedDptId: number | null,
    selectedEmployeeId: number | null,
    startDate: Date | null,
    endDate: Date | null
  ): Observable<DashboardCalendarStatsOfMonths> {
    const isSuper = this.isSuper()

    if (selectedEmployeeId)
      return this.adminDashboardService.getDashboardCalendarStatsOfMonthsByDateRangeAndEmployeeId(selectedEmployeeId, startDate, endDate)

    if (calendarOption === DashboardCalendarOption.DAYS) {
      return isSuper && !selectedDptId
        ? this.adminDashboardService.getDashboardCalendarStatsOfMonthsByDateRange(startDate, endDate)
        : this.adminDashboardService.getDashboardCalendarStatsOfMonthsByDateRangeAndDptId(selectedDptId, startDate, endDate)
    }

    return isSuper && !selectedDptId
      ? this.adminDashboardService.getDashboardCalendarStatsOfMonthsAndEmployeesByDateRange(startDate, endDate)
      : this.adminDashboardService.getDashboardCalendarStatsOfMonthsAndEmployeesByDateRangeAndDptId(selectedDptId, startDate, endDate)
  }

  private isSuper(): boolean {
    return this.loginService.isAdmin() || this.loginService.isSuperLeader()
  }

  public mapChartJsStats(stats: DashboardCalendarStatsOfMonths, isAccepted: boolean): DashboardCalendarChartStatsOfMonths {
    const chartJsStats = new DashboardCalendarChartStatsOfMonths()
    chartJsStats.holidays = this.mapChartJsCounts(stats.holidays, isAccepted)
    chartJsStats.personalMatters = this.mapChartJsCounts(stats.personal_matters, isAccepted)
    chartJsStats.sickLeave = this.mapChartJsCounts(stats.sick_leave, isAccepted)
    chartJsStats.paidLeave = this.mapChartJsCounts(stats.paid_leave, isAccepted)
    chartJsStats.remoteWork = this.mapChartJsCounts(stats.remote_work, isAccepted)

    return chartJsStats
  }

  private mapChartJsCounts(values: DashboardCalendarCountOfMonth[], isAccepted: boolean): DashboardCalendarChartCountOfMonth[] {
    return values.map(item => {
      const count = new DashboardCalendarChartCountOfMonth()
      count.value = isAccepted ? item.accepted : item.pending
      count.month = item.month

      return count
    })
  }

  public mapChartJsData(stats: DashboardCalendarChartStatsOfMonths): ChartDataset<'bar'>[] {
    const holidays = stats.holidays
    const personalMatters = stats.personalMatters
    const sickLeave = stats.sickLeave
    const paidLeave = stats.paidLeave
    const remoteWork = stats.remoteWork

    return [
      this.getDataSet(holidays, this.HOLIDAYS_LABEL, this.HOLIDAYS_COLOR),
      this.getDataSet(personalMatters, this.PERSONAL_MATTERS_LABEL, this.PERSONAL_MATTERS_COLOR),
      this.getDataSet(sickLeave, this.SICK_LEAVE_LABEL, this.SICK_LEAVE_COLOR),
      this.getDataSet(paidLeave, this.PAID_LEAVE_LABEL, this.PAID_LEAVE_COLOR),
      this.getDataSet(remoteWork, this.REMOTE_WORK_LABEL, this.REMOTE_WORK_COLOR)
    ]
  }

  private getDataSet(data: DashboardCalendarChartCountOfMonth[], label: string, backgroundColor: string): ChartDataset<'bar'> {
    return {
      data: this.getValues(data),
      label,
      backgroundColor,
      borderRadius: {
        topLeft: 8,
        topRight: 8,
        bottomLeft: 0,
        bottomRight: 0
      },
      hoverBackgroundColor: this.brightenColor(backgroundColor, 10),
    }
  }

  private brightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16)
    const amt = Math.round(2.55 * percent)

    let r = (num >> 16) + amt
    let g = (num >> 8 & 0x00FF) + amt
    let b = (num & 0x0000FF) + amt

    return (
      '#' +
      ((1 << 24)
        + (Math.min(255, r) << 16)
        + (Math.min(255, g) << 8)
        + Math.min(255, b)
      ).toString(16).slice(1)
    )
  }

  public getChartJSLabels(): string[] {
    return this.MONTHS
  }

  public createChartOptions(): ChartOptions<'bar'> {
    return {
      // Hace que el gráfico se adapte al tamaño del contenedor
      responsive: true,

      // No mantiene la relación de aspecto original; permite que se estire en ancho y alto
      maintainAspectRatio: false,

      // Configuración de animaciones al pintar el gráfico
      animation: {
        duration: 1000,       // duración total de la animación inicial (1 segundo)
        easing: 'easeOutBack' // curva de animación: rebote hacia afuera
      },

      transitions: {
        active: { 
          animation: {
            duration: 0 // hover sin animación
          }
        },
        resize: {
          animation: {
            duration: 1000,   // 1 segundo al redimensionar
            easing: 'easeOutBack'
          }
        }
      },

      // Configuraciones de plugins del gráfico
      plugins: {
        // Leyenda del gráfico
        legend: {
          position: 'top',    // coloca la leyenda arriba del gráfico
          labels: {
            usePointStyle: true,    // usa un “punto” en lugar de cuadrado para cada dataset
            pointStyle: 'rectRounded', // estilo del punto: rectángulo redondeado
            // ⚠️ boxWidth y padding se podrían añadir para tamaño y separación
          }
        },

        // Tooltips que aparecen al pasar el mouse sobre las barras
        tooltip: {
          cornerRadius: 12,  // redondeo de las esquinas del tooltip
          callbacks: {
            // Título del tooltip (normalmente el nombre del dataset)
            title: (tooltipItems) => this.getTooltipTitle(tooltipItems),

            // Valor mostrado dentro del tooltip (formateado)
            label: (tooltipItem) => this.getTooltipValue(tooltipItem),

            // Personalización del “icono” de color del tooltip
            labelColor: (tooltipItem) => this.getTooltipLabelColor(tooltipItem)
            // ⚠️ aquí es donde controlamos color, borderWidth y borderRadius del icono
          }
        }
      },

      // Configuración de los ejes
      scales: {
        // Eje X (horizontal)
        x: {
          grid: {
            color: '#F5F5F5', // color de las líneas de la cuadrícula
            drawTicks: false   // no dibuja pequeñas marcas en los ticks
          },
          ticks: {
            padding: 10        // espacio entre tick y etiqueta
          }
        },

        // Eje Y (vertical)
        y: {
          grid: {
            color: '#F5F5F5', // color de líneas horizontales de la cuadrícula
            drawTicks: false   // sin marcas pequeñas
          },
          ticks: {
            padding: 10,        // espacio entre tick y número
            precision: 0,       // asegura no dejar decimales en las etiquetas
            callback: (value) => this.getYAxeValue(Number(value)) // formatea los números (K, M)
          }
        }
      }
    }
  }

  private getYAxeValue(value: number): string | number {
    if (value >= 1000000) return (value / 1000000) + 'M'

    if (value >= 1000) return (value / 1000) + 'K'

    return value
  }

  private getTooltipTitle(tooltipItems: TooltipItem<'bar'>[]): string {
    const index = tooltipItems[0].datasetIndex

    return [
      this.HOLIDAYS_LABEL,
      this.PERSONAL_MATTERS_LABEL,
      this.SICK_LEAVE_LABEL,
      this.PAID_LEAVE_LABEL,
      this.REMOTE_WORK_LABEL
    ][index]
  }

  private getTooltipValue(tooltipItem: TooltipItem<'bar'>): string {
    const value: number = tooltipItem.parsed.y

    const num = Number(value)

    return ' ' + new Intl.NumberFormat('es-ES').format(num)
  }

  private getTooltipLabelColor(tooltipItem: TooltipItem<'bar'>): TooltipLabelStyle {
    const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex]

    const backgroundColor = dataset.backgroundColor as string

    return {
      borderColor: 'transparent',
      backgroundColor: backgroundColor,
      borderWidth: 0,
      borderRadius: 3
    }
  }

  private getValues(countOfMonth: DashboardCalendarChartCountOfMonth[]): number[] {
    return this.MONTHS.map((month, index) => countOfMonth.find(item => item.month === index + 1)?.value ?? 0)
  }

  public fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return swal.fire(options)
  }
}
